import type { EasyAIClient, RunOptions, RunResult } from "./client";

/** EasyAI instance a chain id belongs to. Ids are per instance, so built-in defaults are keyed by it. */
export type EasyAiEnv = "dev" | "prod";

/** Which EasyAI instance a base URL points at, or null for anything else (local mocks, proxies). */
export function easyAiEnvFor(baseUrl: string): EasyAiEnv | null {
  let host: string;
  try {
    host = new URL(baseUrl).hostname;
  } catch {
    return null;
  }
  if (host === "easyai-dev.cloud.passiolife.com") return "dev";
  if (host === "easyai.passiolife.com") return "prod";
  return null;
}

/**
 * Stand-in id for a chain that is planned but not built yet. It registers the chain (admin can
 * see it and paste the real id later) while the UI shows it as coming soon and the proxy refuses it.
 */
export const placeholderChainId = (n: number) => `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`;
export const isPlaceholderChainId = (id: string | null | undefined) => Boolean(id && /^00000000-0000-4000-8000-\d{12}$/.test(id));

/** One runtime input of a chain, mirroring EasyAI's runtime_inputs. Drives the generated form. */
export interface ChainInputField {
  key: string;
  type: "textarea" | "text" | "number" | "boolean" | "select";
  label: string;
  required?: boolean;
  default?: string | number | boolean;
  placeholder?: string;
  help?: string;
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
  /** Number fields: 0 is shown as this choice (e.g. "Auto") in a dropdown of min..max. */
  autoLabel?: string;
}

/** A demo chain defined in code: what it is and the shape of its input. */
export interface DemoChainDef<Input extends Record<string, unknown> = Record<string, unknown>> {
  key: string;
  title: string;
  description: string;
  /** Page that runs it: "meal_plan" chains are plan types on Meal plans; "tool" chains have their own page. */
  group?: "meal_plan" | "tool";
  /** Demo feature that gates running it. Defaults to `meal_plans`. */
  feature?: string;
  /** Built-in chain id per EasyAI instance. Admin overrides and `EASYAI_CHAIN_IDS` take precedence. */
  defaultChainIds?: Partial<Record<EasyAiEnv, string>>;
  inputs?: ChainInputField[];
  /** Sample requests for the main text input. */
  examples?: string[];
  defaultInput?: Partial<Input>;
  sinkNodeId?: string;
  /**
   * Passio API meal plan type (`POST /napi/tools/mealPlans/{platformRoute}`). Set: runs go through the
   * Passio API, which picks the chain id (LaunchDarkly `meal-plan-*-chain-id`) and adds the plan
   * requirements; the id here only decides whether the type shows as available.
   */
  platformRoute?: string;
}

const MEAL_PLAN_INPUTS: ChainInputField[] = [
  {
    key: "user_request",
    type: "textarea",
    label: "What should the plan look like?",
    required: true,
    placeholder: "Dietary preferences, restrictions, allergies, budget, target macros, household size, cooking time, cuisines…",
  },
  { key: "days", type: "number", label: "Days", default: 0, min: 1, max: 14, autoLabel: "Auto (from request)" },
  { key: "meals_per_day", type: "number", label: "Meals per day", default: 4, min: 1, max: 8 },
  { key: "include_pdf", type: "boolean", label: "Printable PDF", default: true },
];

/**
 * Models follow counts in the prompt far more reliably than separate input fields, so counts chosen
 * in the form are restated as explicit requirements at the end of `user_request`.
 */
export function withPlanRequirements(input: Record<string, unknown>): Record<string, unknown> {
  const request = typeof input.user_request === "string" ? input.user_request.trim() : "";
  if (!request) return input;
  const n = (k: string) => (typeof input[k] === "number" && (input[k] as number) > 0 ? (input[k] as number) : 0);
  const lines: string[] = [];
  const days = n("days");
  if (days) lines.push(`- Number of days: exactly ${days} (Day 1 to Day ${days}). Return all ${days} days, no more and no fewer.`);
  const meals = n("meals_per_day");
  if (meals) lines.push(`- Meals per day: exactly ${meals} on every day.`);
  const household = n("household_size");
  if (household) lines.push(`- Household size: exactly ${household} people.`);
  if (input.adults_only_meals_allowed === false) lines.push("- Every meal must work for everyone in the household, including children.");
  if (!lines.length) return input;
  return { ...input, user_request: `${request}\n\nPLAN REQUIREMENTS (set in the form; these override anything above):\n${lines.join("\n")}` };
}

/** Plan types that share FAST's inputs and output. They exist on prod EasyAI only so far. */
const mealPlanVariant = (key: string, platformRoute: string, title: string, description: string, n: number, prodChainId: string, examples: string[]): DemoChainDef => ({
  key,
  title,
  description,
  group: "meal_plan",
  defaultChainIds: { dev: placeholderChainId(n), prod: prodChainId },
  inputs: MEAL_PLAN_INPUTS,
  examples,
  sinkNodeId: "node-9",
  platformRoute,
});

/**
 * Registry of EasyAI chains the demo knows about.
 *
 * Chain ids can be changed at any time in Admin → Chains without a deploy. Resolution order:
 * admin override, then `EASYAI_CHAIN_IDS` (`{"meal_plan_fast":"<uuid>"}`), then `defaultChainIds`
 * for the EasyAI instance the server talks to. Admins can also add chains that have no entry here;
 * those run on the generic runner on the Meal plans page.
 */
export const DemoChains: Record<string, DemoChainDef> = {
  meal_plan_fast: {
    key: "meal_plan_fast",
    title: "Fast",
    description: "A complete plan in the fewest steps: request and day outlines, recipes per day, Passio-linked ingredients, macro totals and an optional PDF. No tuning loop.",
    group: "meal_plan",
    defaultChainIds: { dev: "894e68c1-f787-41d5-8719-85d55d199a14", prod: "300a84cc-bb4f-43df-a315-cd9c122c184e" },
    inputs: MEAL_PLAN_INPUTS,
    examples: [
      "3 days, vegetarian, high protein (about 140 g a day), around 2,000 kcal. No mushrooms. Quick weekday dinners.",
      "5-day Mediterranean plan for weight loss, 1,700 kcal, gluten free, I love salmon and chickpeas.",
      "One day of easy meals for a student on a budget, no cooking skills, dairy free.",
    ],
    sinkNodeId: "node-9",
    platformRoute: "fast",
  },
  meal_plan_macro_tuned: mealPlanVariant(
    "meal_plan_macro_tuned",
    "macroTuned",
    "Macro tuned",
    "Like Fast, plus a tuning pass that scales ingredient amounts so each day lands on your calorie and macro targets.",
    1,
    "6317f9d9-b6ea-4a45-a59a-afaff80d7e9e",
    ["4 days at 2,200 kcal: 180 g protein, 200 g carbs, 70 g fat. Chicken, rice and veg are fine every day."],
  ),
  meal_plan_budget: mealPlanVariant(
    "meal_plan_budget",
    "budget",
    "Budget",
    "Cheap, pantry-friendly meals that reuse ingredients across days to keep the grocery bill down.",
    2,
    "e16d2a73-33e7-468b-9de7-e27ab43017bb",
    ["A week of dinners for two under $60 total. We have rice, beans, oats and frozen veg already."],
  ),
  meal_plan_athletic: mealPlanVariant(
    "meal_plan_athletic",
    "athletic",
    "Athletic",
    "Fuel for training: meals timed around workouts, higher protein and carbs on training days.",
    3,
    "5bbc5544-3e08-4385-a500-995fddd55ef7",
    ["Marathon training, 5 runs a week, 72 kg. 3 days with a long run on day 3. No pork."],
  ),
  meal_plan_family: {
    key: "meal_plan_family",
    title: "Family",
    description: "Plans for a whole household: reads each member's age, allergies and dislikes, scales portions to the household and keeps meals safe for everyone.",
    group: "meal_plan",
    defaultChainIds: { dev: "fe64f3b1-b6e5-4db5-91c6-e4ee55943de2", prod: "dd67f72a-c99a-4d2c-8220-92b030db6654" },
    inputs: [
      {
        key: "user_request",
        type: "textarea",
        label: "Describe your household",
        required: true,
        placeholder: "Each person's name or role, age, allergies, dislikes and preferences, plus schedule, cooking skill, budget and goals.",
      },
      { key: "days", type: "number", label: "Days", default: 0, min: 1, max: 14, autoLabel: "Auto (from request)" },
      { key: "meals_per_day", type: "number", label: "Meals per day", default: 3, min: 1, max: 6 },
      { key: "household_size", type: "number", label: "Household size", default: 0, min: 1, max: 12, autoLabel: "Auto (from description)" },
      { key: "adults_only_meals_allowed", type: "boolean", label: "Allow adults-only meals", default: false },
      { key: "include_pdf", type: "boolean", label: "Printable PDF", default: true },
    ],
    examples: [
      "2 adults and 2 kids (6 and 9). The 6-year-old is allergic to peanuts, the 9-year-old won't eat anything spicy. We want quick dinners on weeknights.",
      "Me (34, vegetarian), my partner (36, eats everything) and our toddler (2). Dairy free for the toddler. Budget-conscious.",
    ],
    sinkNodeId: "node-10",
    platformRoute: "family",
  },
  gi_coach: {
    key: "gi_coach",
    title: "GI coach (GLP-1)",
    description: "Educational coach for people on GLP-1 treatment: how a meal or symptom may sit, what might help, with safety escalation and cited sources. North America profile.",
    group: "tool",
    feature: "gi_coach",
    defaultChainIds: { dev: "fa1d82b9-64d8-4f99-9960-7b16fb109bec", prod: "2244ab05-5bda-4f61-851f-ba330c8c8b9d" },
    sinkNodeId: "node-3",
  },
  grocery_swaps: {
    key: "grocery_swaps",
    title: "Keto grocery swaps",
    description: "Takes a grocery list and suggests keto-friendly, less processed replacements from the Passio catalog, ranked by keto fit and how processed they are.",
    group: "tool",
    feature: "grocery_swaps",
    defaultChainIds: { dev: "a4ae0bce-b6d5-4c4a-a481-9ba81f5d3e4f" },
    sinkNodeId: "node-6",
  },
};

/** A chain as the server hands it to the browser: definition plus the id currently in effect. */
export interface ResolvedChain {
  key: string;
  title: string;
  description: string;
  chainId: string;
  /** Placeholder id: listed so users see it's coming, but it can't run yet. */
  placeholder: boolean;
  group: DemoChainDef["group"] | null;
  feature: string;
  inputs: ChainInputField[];
  examples: string[];
  defaultInput: Record<string, unknown>;
  sinkNodeId?: string | null;
  /** Passio API meal plan type it runs through (see `DemoChainDef.platformRoute`). */
  platformRoute?: string | null;
}

/** Clients `runDemoChain` picks from: EasyAI directly, or the Passio API meal plan routes. */
export interface DemoChainClients {
  easyai: EasyAIClient;
  mealPlans: EasyAIClient;
}

export const CHAIN_KEY_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

/** Demo page (tool id) a chain runs on: tool chains have their own page, everything else is on Meal plans. */
export const chainToolId = (c: { key: string; group?: string | null }) => (c.group === "tool" ? c.key : "meal_plans");

/** Run a chain by registry key using the resolved chain list from `/api/me`. Meal plan types go through the Passio API. */
export function runDemoChain<T>(
  clients: DemoChainClients,
  chains: ResolvedChain[],
  key: string,
  input: Record<string, unknown>,
  opts?: RunOptions,
): Promise<RunResult<T>> {
  const chain = chains.find((c) => c.key === key);
  if (!chain) throw new Error(`No chain id configured for "${key}"`);
  if (chain.placeholder) throw new Error(`"${chain.title}" isn't available yet.`);
  const runOpts = { chainKey: chain.key, label: chain.title, ...opts, sinkNodeId: chain.sinkNodeId ?? undefined };
  const merged = { ...chain.defaultInput, ...input };
  if (!chain.platformRoute) return clients.easyai.run<T>(chain.chainId, merged, runOpts);
  // The Passio API refuses input keys a plan type doesn't take.
  const allowed = new Set(chain.inputs.map((f) => f.key));
  return clients.mealPlans.run<T>(chain.platformRoute, Object.fromEntries(Object.entries(merged).filter(([k]) => allowed.has(k))), runOpts);
}
